import React, { useState } from 'react';
import { IoPerson, IoSchool, IoBriefcase, IoHeart, IoInformationCircleOutline, IoPersonCircle, IoAdd, IoBusinessOutline } from 'react-icons/io5';
import { FamilyData, FamilyMember } from '@/services/familyService';
import MissingDataIndicator from '@/components/MissingDataIndicator';
import './FamilyDataDisplay.css';



interface FamilyDataDisplayProps {
  familyData: FamilyData;
  onEdit?: (member: FamilyMember) => void;
  missingInfo?: string[];
  onShowMissingInfo?: () => void;
}

interface AddFamilyMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (memberData: Partial<FamilyMember>) => void;
}

interface EditFamilyMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (memberData: FamilyMember) => void;
  member: FamilyMember | null;
}

// Add Family Member Modal Component
const AddFamilyMemberModal: React.FC<AddFamilyMemberModalProps> = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState<Partial<FamilyMember> & { relationship_to_parents?: string }>({
    relationship: undefined,
    first_name: '',
    last_name: '',
    occupation: '',
    employment_status: undefined,
    highest_education: '',
    education_level: undefined,
    living_status: 'living',
    relationship_to_parents: '',
    age: undefined
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
    onClose();
    setFormData({
      relationship: undefined,
      first_name: '',
      last_name: '',
      occupation: '',
      employment_status: undefined,
      highest_education: '',
      education_level: undefined,
      living_status: 'living',
      relationship_to_parents: '',
      age: undefined
    });
  };

  if (!isOpen) return null;

  return (
    <div className="family-modal-overlay">
      <div className="family-modal">
        <div className="family-modal-header">
          <h3>Add Family Member</h3>
          <button onClick={onClose} className="family-modal-close">×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="family-modal-form">
          <div className="family-modal-field">
            <label>Relationship</label>
            <select 
              value={formData.relationship || ''} 
              onChange={(e) => {
                const value = e.target.value;
                setFormData({...formData, relationship: value ? value as FamilyMember['relationship'] : undefined});
              }}
              required
            >
              <option value="">Select relationship</option>
              <option value="stepmother">Step-mother</option>
              <option value="stepfather">Step-father</option>
              <option value="sibling">Sibling</option>
              <option value="guardian">Guardian</option>
            </select>
          </div>

          <div className="family-modal-row">
            <div className="family-modal-field">
              <label>First Name</label>
              <input 
                type="text" 
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                required
              />
            </div>
            <div className="family-modal-field">
              <label>Last Name</label>
              <input 
                type="text" 
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              />
            </div>
          </div>

          {formData.relationship === 'sibling' && (
            <>
              <div className="family-modal-field">
                <label>Age</label>
                <input 
                  type="number" 
                  value={formData.age || ''}
                  onChange={(e) => setFormData({...formData, age: e.target.value ? parseInt(e.target.value) : undefined})}
                  min="1"
                  max="30"
                  placeholder="Enter age"
                />
              </div>
              <div className="family-modal-field">
                <label>Education Level</label>
                <select 
                  value={formData.education_level || ''}
                  onChange={(e) => setFormData({...formData, education_level: e.target.value as any})}
                >
                  <option value="">Select education level</option>
                  <option value="some_grade_primary">Elementary School</option>
                  <option value="some_high_secondary">Middle School</option>
                  <option value="graduated_high_secondary">High School</option>
                  <option value="some_college_university">Some College</option>
                  <option value="graduated_college_university">College Graduate</option>
                </select>
              </div>
              <div className="family-modal-field">
                <label>Relationship to Parents</label>
                <select 
                  value={formData.relationship_to_parents}
                  onChange={(e) => setFormData({...formData, relationship_to_parents: e.target.value})}
                >
                  <option value="">Select type</option>
                  <option value="full_sibling">Full sibling (same parents)</option>
                  <option value="half_sibling">Half sibling (one shared parent)</option>
                  <option value="step_sibling">Step sibling (no shared parents)</option>
                </select>
              </div>
            </>
          )}

          {formData.relationship && ['stepmother', 'stepfather'].includes(formData.relationship) && (
            <div className="family-modal-field">
              <label>Married To</label>
              <select 
                value={formData.relationship_to_parents}
                onChange={(e) => setFormData({...formData, relationship_to_parents: e.target.value})}
                required
              >
                <option value="">Select parent</option>
                <option value="mother">Mother</option>
                <option value="father">Father</option>
              </select>
            </div>
          )}

          {formData.relationship && ['stepmother', 'stepfather', 'guardian'].includes(formData.relationship) && (
            <>
              <div className="family-modal-field">
                <label>Occupation</label>
                <select 
                  value={formData.occupation}
                  onChange={(e) => setFormData({...formData, occupation: e.target.value})}
                >
                  <option value="">Select occupation</option>
                  <option value="Accountant or actuary">Accountant or actuary</option>
                  <option value="Actor or entertainer">Actor or entertainer</option>
                  <option value="Architect or urban planner">Architect or urban planner</option>
                  <option value="Artist">Artist</option>
                  <option value="Business (clerical)">Business (clerical)</option>
                  <option value="Business executive (management, administrator)">Business executive (management, administrator)</option>
                  <option value="Business owner or proprietor">Business owner or proprietor</option>
                  <option value="Business salesperson or buyer">Business salesperson or buyer</option>
                  <option value="Clergy (minister, priest)">Clergy (minister, priest)</option>
                  <option value="Clergy (other religious)">Clergy (other religious)</option>
                  <option value="Clinical psychologist">Clinical psychologist</option>
                  <option value="College administrator/staff">College administrator/staff</option>
                  <option value="College teacher">College teacher</option>
                  <option value="Computer programmer or analyst">Computer programmer or analyst</option>
                  <option value="Conservationist or forester">Conservationist or forester</option>
                  <option value="Dentist (including orthodontist)">Dentist (including orthodontist)</option>
                  <option value="Dietitian or nutritionist">Dietitian or nutritionist</option>
                  <option value="Engineer">Engineer</option>
                  <option value="Farmer or rancher">Farmer or rancher</option>
                  <option value="Foreign service worker (including diplomat)">Foreign service worker (including diplomat)</option>
                  <option value="Homemaker (full-time)">Homemaker (full-time)</option>
                  <option value="Interior decorator (including designer)">Interior decorator (including designer)</option>
                  <option value="Lab technician or hygienist">Lab technician or hygienist</option>
                  <option value="Laborer">Laborer</option>
                  <option value="Law enforcement officer">Law enforcement officer</option>
                  <option value="Lawyer (attorney) or judge">Lawyer (attorney) or judge</option>
                  <option value="Military service (career)">Military service (career)</option>
                  <option value="Musician (performer, composer)">Musician (performer, composer)</option>
                  <option value="Nurse">Nurse</option>
                  <option value="Optometrist">Optometrist</option>
                  <option value="Pharmacist">Pharmacist</option>
                  <option value="Physician">Physician</option>
                  <option value="Policymaker/Government">Policymaker/Government</option>
                  <option value="School counselor">School counselor</option>
                  <option value="School principal or superintendent">School principal or superintendent</option>
                  <option value="Scientific researcher">Scientific researcher</option>
                  <option value="Skilled trades">Skilled trades</option>
                  <option value="Social, welfare, or recreation worker">Social, welfare, or recreation worker</option>
                  <option value="Teacher or administrator (elementary)">Teacher or administrator (elementary)</option>
                  <option value="Teacher or administrator (secondary)">Teacher or administrator (secondary)</option>
                  <option value="Therapist (physical, occupational, speech)">Therapist (physical, occupational, speech)</option>
                  <option value="Veterinarian">Veterinarian</option>
                  <option value="Writer or journalist">Writer or journalist</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="family-modal-field">
                <label>Employment Status</label>
                <select 
                  value={formData.employment_status || ''}
                  onChange={(e) => setFormData({...formData, employment_status: e.target.value as any})}
                >
                  <option value="">Select employment status</option>
                  <option value="employed">Employed</option>
                  <option value="self_employed">Self-Employed</option>
                  <option value="unemployed">Unemployed</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
              <div className="family-modal-field">
                <label>Education Level</label>
                <select 
                  value={formData.education_level || ''}
                  onChange={(e) => setFormData({...formData, education_level: e.target.value as any})}
                >
                  <option value="">Select education level</option>
                  <option value="none">None</option>
                  <option value="some_grade_primary">Some grade/primary school</option>
                  <option value="completed_grade_primary">Completed grade/primary school</option>
                  <option value="some_high_secondary">Some high/secondary school</option>
                  <option value="graduated_high_secondary">Graduated from high/secondary school (or equivalent)</option>
                  <option value="some_trade_community">Some trade school or community college</option>
                  <option value="graduated_trade_community">Graduated from trade school or community college</option>
                  <option value="some_college_university">Some college/university</option>
                  <option value="graduated_college_university">Graduated from college/university</option>
                  <option value="graduate_degree">Graduate degree</option>
                </select>
              </div>
            </>
          )}

          <div className="family-modal-actions">
            <button type="button" onClick={onClose} className="family-modal-cancel">
              Cancel
            </button>
            <button type="submit" className="family-modal-submit">
              Add Family Member
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Family Member Modal Component
const EditFamilyMemberModal: React.FC<EditFamilyMemberModalProps> = ({ isOpen, onClose, onSave, member }) => {
  const [formData, setFormData] = useState<FamilyMember>({
    id: '',
    relationship: 'mother',
    living_status: 'living',
    first_name: '',
    last_name: '',
    occupation: '',
    employment_status: undefined,
    highest_education: '',
    education_level: undefined,
  });

  React.useEffect(() => {
    if (member && isOpen) {
      setFormData({ ...member });
    }
  }, [member, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const handleFieldClick = (field: keyof FamilyMember, currentValue: any) => {
    // For quick editing, we can focus on empty fields
    if (!currentValue) {
      const input = document.querySelector(`[name="${field}"]`) as HTMLInputElement | HTMLSelectElement;
      if (input) {
        input.focus();
      }
    }
  };

  if (!isOpen || !member) return null;

  return (
    <div className="family-modal-overlay">
      <div className="family-modal">
        <div className="family-modal-header">
          <h3>Edit {member.first_name || 'Family Member'}</h3>
          <button onClick={onClose} className="family-modal-close">×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="family-modal-form">
          <div className="family-modal-row">
            <div className="family-modal-field">
              <label>First Name</label>
              <input 
                type="text" 
                name="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                required
              />
            </div>
            <div className="family-modal-field">
              <label>Last Name</label>
              <input 
                type="text" 
                name="last_name"
                value={formData.last_name || ''}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              />
            </div>
          </div>

          <div className="family-modal-field">
            <label>Occupation</label>
            <select 
              name="occupation"
              value={formData.occupation || ''}
              onChange={(e) => setFormData({...formData, occupation: e.target.value})}
            >
              <option value="">Select occupation</option>
              <option value="Accountant or actuary">Accountant or actuary</option>
              <option value="Actor or entertainer">Actor or entertainer</option>
              <option value="Architect or urban planner">Architect or urban planner</option>
              <option value="Artist">Artist</option>
              <option value="Business (clerical)">Business (clerical)</option>
              <option value="Business executive (management, administrator)">Business executive (management, administrator)</option>
              <option value="Business owner or proprietor">Business owner or proprietor</option>
              <option value="Business salesperson or buyer">Business salesperson or buyer</option>
              <option value="Clergy (minister, priest)">Clergy (minister, priest)</option>
              <option value="Clergy (other religious)">Clergy (other religious)</option>
              <option value="Clinical psychologist">Clinical psychologist</option>
              <option value="College administrator/staff">College administrator/staff</option>
              <option value="College teacher">College teacher</option>
              <option value="Computer programmer or analyst">Computer programmer or analyst</option>
              <option value="Conservationist or forester">Conservationist or forester</option>
              <option value="Dentist (including orthodontist)">Dentist (including orthodontist)</option>
              <option value="Dietitian or nutritionist">Dietitian or nutritionist</option>
              <option value="Engineer">Engineer</option>
              <option value="Farmer or rancher">Farmer or rancher</option>
              <option value="Foreign service worker (including diplomat)">Foreign service worker (including diplomat)</option>
              <option value="Homemaker (full-time)">Homemaker (full-time)</option>
              <option value="Interior decorator (including designer)">Interior decorator (including designer)</option>
              <option value="Lab technician or hygienist">Lab technician or hygienist</option>
              <option value="Laborer">Laborer</option>
              <option value="Law enforcement officer">Law enforcement officer</option>
              <option value="Lawyer (attorney) or judge">Lawyer (attorney) or judge</option>
              <option value="Military service (career)">Military service (career)</option>
              <option value="Musician (performer, composer)">Musician (performer, composer)</option>
              <option value="Nurse">Nurse</option>
              <option value="Optometrist">Optometrist</option>
              <option value="Pharmacist">Pharmacist</option>
              <option value="Physician">Physician</option>
              <option value="Policymaker/Government">Policymaker/Government</option>
              <option value="School counselor">School counselor</option>
              <option value="School principal or superintendent">School principal or superintendent</option>
              <option value="Scientific researcher">Scientific researcher</option>
              <option value="Skilled trades">Skilled trades</option>
              <option value="Social, welfare, or recreation worker">Social, welfare, or recreation worker</option>
              <option value="Teacher or administrator (elementary)">Teacher or administrator (elementary)</option>
              <option value="Teacher or administrator (secondary)">Teacher or administrator (secondary)</option>
              <option value="Therapist (physical, occupational, speech)">Therapist (physical, occupational, speech)</option>
              <option value="Veterinarian">Veterinarian</option>
              <option value="Writer or journalist">Writer or journalist</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="family-modal-field">
            <label>Employment Status</label>
            <select 
              name="employment_status"
              value={formData.employment_status || ''}
              onChange={(e) => setFormData({...formData, employment_status: e.target.value as any})}
            >
              <option value="">Select employment status</option>
              <option value="employed">Employed</option>
              <option value="self_employed">Self-Employed</option>
              <option value="unemployed">Unemployed</option>
              <option value="retired">Retired</option>
            </select>
          </div>

          <div className="family-modal-field">
            <label>Education Level</label>
            <select 
              name="education_level"
              value={formData.education_level || ''}
              onChange={(e) => setFormData({...formData, education_level: e.target.value as any})}
            >
              <option value="">Select education level</option>
              <option value="none">None</option>
              <option value="some_grade_primary">Some grade/primary school</option>
              <option value="completed_grade_primary">Completed grade/primary school</option>
              <option value="some_high_secondary">Some high/secondary school</option>
              <option value="graduated_high_secondary">Graduated from high/secondary school (or equivalent)</option>
              <option value="some_trade_community">Some trade school or community college</option>
              <option value="graduated_trade_community">Graduated from trade school or community college</option>
              <option value="some_college_university">Some college/university</option>
              <option value="graduated_college_university">Graduated from college/university</option>
              <option value="graduate_degree">Graduate degree</option>
            </select>
          </div>

          <div className="family-modal-actions">
            <button type="button" onClick={onClose} className="family-modal-cancel">
              Cancel
            </button>
            <button type="submit" className="family-modal-submit">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FamilyDataDisplay: React.FC<FamilyDataDisplayProps> = ({ 
  familyData, 
  onEdit, 
  missingInfo = [], 
  onShowMissingInfo 
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [localFamilyData, setLocalFamilyData] = useState<FamilyData>(familyData);

  // Sync local state with prop changes
  React.useEffect(() => {
    setLocalFamilyData(familyData);
  }, [familyData]);

  const handleEditMember = (member: FamilyMember) => {
    setEditingMember(member);
    setShowEditModal(true);
    console.log('Editing member:', member);
  };

  const handleSaveMember = (updatedMember: FamilyMember) => {
    // TODO: Integrate with API to save family member changes
    console.log('Saving member:', updatedMember);
    if (onEdit) {
      onEdit(updatedMember);
    }
    setEditingMember(null);
  };
  const formatName = (member: FamilyMember) => {
    const parts = [];
    if (member.prefix) parts.push(member.prefix);
    if (member.first_name) parts.push(member.first_name);
    if (member.middle_name) parts.push(member.middle_name);
    if (member.last_name) parts.push(member.last_name);
    if (member.suffix) parts.push(member.suffix);
    return parts.join(' ');
  };

  const getRelationshipIcon = (relationship: string) => {
    switch (relationship.toLowerCase()) {
      case 'mother':
      case 'father':
      case 'stepmother':
      case 'stepfather':
      case 'guardian':
        return <IoPerson className="text-blue-500" />;
      case 'sibling':
        return <IoHeart className="text-pink-500" />;
      default:
        return <IoPerson className="text-gray-500" />;
    }
  };

  const formatRelationship = (relationship: string) => {
    return relationship.charAt(0).toUpperCase() + relationship.slice(1);
  };

  // Get family members from the local data structure - handle both flat and nested structures
  const familyMembers = localFamilyData.family_members || (localFamilyData as any)['Family Members'] || [];
  const siblings = (localFamilyData as any).siblings || (localFamilyData as any)['Siblings'] || [];
  const allMembers = [...familyMembers, ...siblings];
  
  // Get relationship status for connecting lines
  const familyStructure = (localFamilyData as any).family_structure || (localFamilyData as any)['Family Structure'];
  const relationshipStatus = familyStructure?.parents_relationship_status || 
                           localFamilyData.parents_relationship_status || 'married';


  
  if (!localFamilyData || allMembers.length === 0) {
    return (
      <div className="family-data-display-empty">
        <IoInformationCircleOutline className="family-data-display-empty-icon" />
        <p className="family-data-display-empty-text">No family information available yet.</p>
      </div>
    );
  }

  // Organize family members by relationship with enhanced logic
  const biologicalParents = allMembers.filter(member => 
    ['mother', 'father'].includes(member.relationship?.toLowerCase())
  );
  const stepParents = allMembers.filter(member => 
    ['stepmother', 'stepfather'].includes(member.relationship?.toLowerCase())
  );
  const guardians = allMembers.filter(member => 
    member.relationship?.toLowerCase() === 'guardian'
  );
  const siblingsOnly = allMembers.filter(member => 
    member.relationship?.toLowerCase() === 'sibling'
  );

  // Organize parents with step-parents next to their biological parent
  const organizeParentsWithStepParents = () => {
    const organizedParents = [];
    
    // Add biological parents first
    biologicalParents.forEach(parent => {
      organizedParents.push(parent);
      
      // Add step-parents married to this biological parent
      const marriedStepParents = stepParents.filter(stepParent => {
        const marriedTo = (stepParent as any).relationship_to_parents;
        return marriedTo === parent.relationship;
      });
      
      organizedParents.push(...marriedStepParents);
    });
    
    // Add any remaining step-parents or guardians
    const remainingStepParents = stepParents.filter(stepParent => {
      const marriedTo = (stepParent as any).relationship_to_parents;
      return !marriedTo || !biologicalParents.some(p => p.relationship === marriedTo);
    });
    
    organizedParents.push(...remainingStepParents, ...guardians);
    
    return organizedParents;
  };

  const parents = organizeParentsWithStepParents();

  // Simplified connector logic - only what's needed
  const renderSimpleParentConnector = () => {
    // Only show connector if there are exactly 2 parents
    if (parents.length === 2) {
      return (
        <div className="family-tree-simple-connector-horizontal">
          <div className="family-tree-connector-line"></div>
          <div className="family-tree-connector-label">
            {formatRelationshipStatus(relationshipStatus)}
          </div>
        </div>
      );
    }
    return null;
  };

  // Render step-parent connectors
  const renderStepParentConnectors = (): React.ReactNode[] => {
    const connectors: React.ReactNode[] = [];
    
    // Find step-parents and their positions
    parents.forEach((parent, index) => {
      if (['stepmother', 'stepfather'].includes(parent.relationship)) {
        const marriedTo = (parent as any).relationship_to_parents;
        if (marriedTo) {
          connectors.push(
            <div key={`step-${index}`} className={`family-tree-step-connector step-connector-${index}`}>
              <div className="family-tree-connector-line"></div>
              <div className="family-tree-connector-label">Remarried</div>
            </div>
          );
        }
      }
    });
    
    return connectors;
  };

  // Calculate children layout for proper centering
  const totalChildren = siblingsOnly.length + 1; // +1 for student
  const childrenWidth = totalChildren * 200 + (totalChildren - 1) * 40; // card width + gaps

  const formatRelationshipStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatEmploymentStatus = (status: string) => {
    const statusMap = {
      'employed': 'Employed',
      'self_employed': 'Self-Employed',
      'unemployed': 'Unemployed',
      'retired': 'Retired'
    };
    return statusMap[status as keyof typeof statusMap] || status;
  };

  const formatEducationLevel = (level: string) => {
    const levelMap = {
      'none': 'None',
      'some_grade_primary': 'Some grade/primary school',
      'completed_grade_primary': 'Completed grade/primary school',
      'some_high_secondary': 'Some high/secondary school',
      'graduated_high_secondary': 'Graduated from high/secondary school (or equivalent)',
      'some_trade_community': 'Some trade school or community college',
      'graduated_trade_community': 'Graduated from trade school or community college',
      'some_college_university': 'Some college/university',
      'graduated_college_university': 'Graduated from college/university',
      'graduate_degree': 'Graduate degree'
    };
    return levelMap[level as keyof typeof levelMap] || level;
  };

  const renderFamilyMemberCard = (member: FamilyMember, isStudent = false) => {
    const isSibling = member.relationship === 'sibling';
    return (
    <div 
      className={`family-tree-card ${isStudent ? 'family-tree-card-student' : ''} ${isSibling ? 'family-tree-card-sibling' : ''} ${!isStudent ? 'family-tree-card-clickable' : ''}`}
      onClick={!isStudent ? () => handleEditMember(member) : undefined}
    >
      <div className="family-tree-card-header">
        <div className="family-tree-card-icon">
          {isStudent ? (
            <IoPersonCircle className="text-blue-600" />
          ) : (
            getRelationshipIcon(member.relationship)
          )}
        </div>
        <div className="family-tree-card-info">
          <h5 className="family-tree-card-name">
            {isStudent ? 'You' : formatName(member)}
          </h5>
          <p className="family-tree-card-relationship">
            {isStudent ? 'Student' : formatRelationship(member.relationship)}
            {member.living_status === 'deceased' && (
              <span className="family-tree-card-deceased">
                {member.date_deceased ? ` (deceased ${member.date_deceased})` : ' (deceased)'}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="family-tree-card-details">
        {/* Student Information */}
        {isStudent && (
          <div className="family-tree-card-student-info">
            {member.age && (
              <div className="family-tree-card-detail">
                <IoPerson className="family-tree-card-detail-icon" />
                <span>Age {member.age}</span>
              </div>
            )}
            {member.highest_education && (
              <div className="family-tree-card-detail">
                <IoSchool className="family-tree-card-detail-icon" />
                <span>{member.highest_education}</span>
              </div>
            )}
          </div>
        )}

        {/* Parent/Guardian Information - Always show with placeholders */}
        {!isStudent && (
          <>
            {/* Occupation - Always show */}
            <div className="family-tree-card-detail" onClick={(e) => {
              if (!member.occupation_category && !member.occupation) {
                e.stopPropagation();
                handleEditMember(member);
              }
            }}>
              <IoBriefcase className={`family-tree-card-detail-icon ${!member.occupation_category && !member.occupation ? 'family-tree-card-detail-icon-placeholder' : ''}`} />
              <span className={!member.occupation_category && !member.occupation ? 'family-tree-card-detail-placeholder' : ''}>
                {member.occupation_category || member.occupation || 'Click to add occupation'}
              </span>
            </div>

            {/* Employment Status - Always show */}
            <div className="family-tree-card-detail" onClick={(e) => {
              if (!member.employment_status) {
                e.stopPropagation();
                handleEditMember(member);
              }
            }}>
              <IoBusinessOutline className={`family-tree-card-detail-icon ${!member.employment_status ? 'family-tree-card-detail-icon-placeholder' : ''}`} />
              <span className={!member.employment_status ? 'family-tree-card-detail-placeholder' : ''}>
                {member.employment_status ? formatEmploymentStatus(member.employment_status) : 'Click to add employment status'}
              </span>
            </div>
            
            {/* Education Level Summary - Always show */}
            <div className="family-tree-card-detail" onClick={(e) => {
              if (!member.education_level && !member.highest_education) {
                e.stopPropagation();
                handleEditMember(member);
              }
            }}>
              <IoSchool className={`family-tree-card-detail-icon ${!member.education_level && !member.highest_education ? 'family-tree-card-detail-icon-placeholder' : ''}`} />
              <span className={!member.education_level && !member.highest_education ? 'family-tree-card-detail-placeholder' : ''}>
                {member.education_level ? formatEducationLevel(member.education_level) : (member.highest_education || 'Click to add education level')}
              </span>
            </div>
            
            {/* Colleges Attended - Compact Format */}
            {member.colleges && member.colleges.length > 0 && (
              <div className="family-tree-card-colleges-compact">
                {member.colleges.slice(0, 2).map((college, index) => (
                  <div key={index} className="family-tree-card-detail">
                    <IoSchool className="family-tree-card-detail-icon" />
                    <span className="family-tree-card-college-compact">
                      <span className="family-tree-card-college-name">{college.name}</span>
                      {(college.degree_category || college.degree) && (
                        <>
                          <span className="family-tree-card-college-separator"> • </span>
                          <span className="family-tree-card-college-degree">{college.degree_category || college.degree}</span>
                        </>
                      )}
                      {college.graduation_year && (
                        <>
                          <span className="family-tree-card-college-separator"> • </span>
                          <span className="family-tree-card-college-year">{college.graduation_year}</span>
                        </>
                      )}
                    </span>
                  </div>
                ))}
                {member.colleges.length > 2 && (
                  <div className="family-tree-card-detail">
                    <IoSchool className="family-tree-card-detail-icon" />
                    <span className="family-tree-card-college-more">
                      +{member.colleges.length - 2} more college{member.colleges.length > 3 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {/* Simplified sibling information - only age and education */}
            {member.relationship === 'sibling' && (
              <div className="family-tree-card-sibling-info">
                {member.age && (
                  <div className="family-tree-card-detail">
                    <IoPerson className="family-tree-card-detail-icon" />
                    <span>Age {member.age}</span>
                  </div>
                )}
                {member.education_level ? (
                  <div className="family-tree-card-detail">
                    <IoSchool className="family-tree-card-detail-icon" />
                    <span>{formatEducationLevel(member.education_level)}</span>
                  </div>
                ) : (member as any).current_status ? (
                  <div className="family-tree-card-detail">
                    <IoSchool className="family-tree-card-detail-icon" />
                    <span>{(member as any).current_status.replace('_', ' ')}</span>
                  </div>
                ) : (
                  <div className="family-tree-card-detail" onClick={(e) => {
                    e.stopPropagation();
                    handleEditMember(member);
                  }}>
                    <IoSchool className="family-tree-card-detail-icon family-tree-card-detail-icon-placeholder" />
                    <span className="family-tree-card-detail-placeholder">Click to add education</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Click to edit hint */}
      {!isStudent && (
        <div className="family-tree-card-edit-hint">
          Click to edit details
        </div>
      )}
    </div>
    );
  };

  const handleAddFamilyMember = (memberData: Partial<FamilyMember>) => {
    // Create a new family member with a temporary ID
    const newMember: FamilyMember = {
      id: `temp_${Date.now()}`,
      relationship: memberData.relationship || 'sibling',
      living_status: memberData.living_status || 'living',
      first_name: memberData.first_name || '',
      last_name: memberData.last_name || '',
      occupation: memberData.occupation || '',
      employment_status: memberData.employment_status,
      highest_education: memberData.highest_education || '',
      education_level: memberData.education_level,
      age: memberData.age,
      colleges: memberData.colleges || [],
      ...memberData
    };

    // Update local family data
    const updatedFamilyData = { ...localFamilyData };
    
    // Determine where to add the new member
    if (newMember.relationship === 'sibling') {
      // Add to siblings array
      const siblings = (updatedFamilyData as any)['Siblings'] || [];
      (updatedFamilyData as any)['Siblings'] = [...siblings, newMember];
    } else {
      // Add to family members array (for step-parents, guardians)
      const familyMembers = (updatedFamilyData as any)['Family Members'] || [];
      (updatedFamilyData as any)['Family Members'] = [...familyMembers, newMember];
    }

    // Update local state
    setLocalFamilyData(updatedFamilyData);
    
    console.log('Added family member:', newMember);
    // TODO: Integrate with API to persist the change
  };

  return (
    <div className="family-data-display">
      <div className="family-tree-header">
        <div className="family-tree-header-left">
          <h4 className="family-tree-title">Your Family Tree</h4>
          {missingInfo.length > 0 && onShowMissingInfo && (
            <MissingDataIndicator 
              missingCount={missingInfo.length}
              onClick={onShowMissingInfo}
              className="compact"
            />
          )}
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="family-tree-add-button"
        >
          <IoAdd />
          Add Family Member
        </button>
      </div>
      
      <div className="family-tree-container">
        {/* Parents Row */}
        {parents.length > 0 && (
          <div className="family-tree-row family-tree-parents">
            {parents.map((parent, index) => (
              <div key={index} className="family-tree-member">
                {renderFamilyMemberCard(parent)}
              </div>
            ))}
            
            {/* Simple parent connector */}
            {renderSimpleParentConnector()}
            
            {/* Step-parent connectors */}
            {renderStepParentConnectors()}
          </div>
        )}

        {/* Single vertical connector from parents to children */}
        {parents.length > 0 && totalChildren > 0 && (
          <div className="family-tree-vertical-connector">
            <div className="family-tree-connector-line-vertical"></div>
          </div>
        )}

        {/* Children Row (Student + Siblings) - Centered */}
        <div className="family-tree-row family-tree-children">
          {/* Horizontal connector spanning all siblings */}
          {totalChildren > 1 && (
            <div 
              className="family-tree-siblings-connector"
              style={{ width: `${(totalChildren - 1) * 220}px` }}
            >
              <div className="family-tree-connector-line-horizontal"></div>
            </div>
          )}
          
          {/* Student (You) - Always first */}
          <div className="family-tree-member">
            {renderFamilyMemberCard({
              first_name: 'You',
              relationship: 'student',
              age: 17, // Default high school age
              highest_education: 'High School'
            } as any, true)}
          </div>
          
          {/* Siblings - Ordered and centered */}
          {siblingsOnly.map((sibling, index) => (
            <div key={index} className="family-tree-member">
              {renderFamilyMemberCard(sibling)}
            </div>
          ))}
        </div>
      </div>

      {/* Add Family Member Modal */}
      <AddFamilyMemberModal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddFamilyMember}
      />

      {/* Edit Family Member Modal */}
      <EditFamilyMemberModal 
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveMember}
        member={editingMember}
      />
    </div>
  );
};

export default FamilyDataDisplay;